import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import styled from 'styled-components';
import { Bookmark, Pin, Search } from 'iconoir-react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button, Drawer, DrawerPosition, Heading, Panel, Table } from '@savgroup-front-common/core';
import { Autocomplete, Input } from '@savgroup-front-common/core/src/atoms/Form';
import { ChevronDownIcon } from '@savgroup-front-common/core/src/protons/icons';
import { Tag } from '@savgroup-front-common/core/src/atoms/Tag';

import { useCurrentUserDisplayName } from '../hooks/useCurrentUserDisplayName';
import { SUPERVISEUR_OPTIONS } from '../constants/inventorySuperviseurOptions';
import { useInventoryViewMode } from '../context/InventoryViewModeContext';
import type { InventoryRow } from '../types/inventoryRow';
import {
  applyManualLinesToInventoryRow,
  POC_INVENTORY_MANUAL_PIECE_EVENT,
} from '../utils/inventoryManualPieceStorage';
import {
  BOX_MARTIN_NEUILLY_ADDITIONAL_INVENTORIES,
  createBoxMartinNeuillyInventoryRow,
} from '../data/inventoryBoxMartinDataset';
import { getDemoInventoryListRow } from '../data/inventoryDemoDataset';
import { applyClosureToInventoryRow, POC_INVENTORY_CLOSURE_EVENT } from '../utils/inventoryClosureStorage';
import {
  formatSignedAmount,
  getAmountEcartVariant,
  resolveInventoryAmountEcarts,
} from '../utils/inventoryAmountEcarts';
import {
  applyInventoryNomToRow,
  persistInventoryNom,
  POC_INVENTORY_NOM_EVENT,
} from '../utils/inventoryNomStorage';
import {
  deleteStockLaunchedInventoryByReference,
  loadStockLaunchedInventories,
  POC_INVENTORIES_FROM_STOCK_EVENT,
} from '../utils/stockLaunchedInventoryStorage';

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

type InventoryFilterKey = 'nom' | 'site' | 'stock' | 'statut';

interface InventoryFormState {
  nom: string;
  site: string;
  stock: string;
  superviseur: string;
  inventoriste: string;
}

const INVENTORISTE_OPTIONS: FilterOption[] = [
  { value: 'Nicolas Petit', label: 'Nicolas Petit' },
  { value: 'Julie Martin', label: 'Julie Martin' },
  { value: 'Jamel Messaoudi', label: 'Jamel Messaoudi' },
  { value: 'José Alvarez', label: 'José Alvarez' },
  { value: 'Camille Robert', label: 'Camille Robert' },
  { value: 'Lucie Bernard', label: 'Lucie Bernard' },
];

const MOCK_INVENTORIES: InventoryRow[] = [
  createBoxMartinNeuillyInventoryRow({
    reference: 'INV-2026-001',
    nom: 'Inventaire Box Martin — Neuilly',
    stock: 'Box de Martin',
  }),
  ...BOX_MARTIN_NEUILLY_ADDITIONAL_INVENTORIES.map(createBoxMartinNeuillyInventoryRow),
  {
    reference: 'INV-2026-002',
    nom: 'Inventaire Box Jérome — Aubagne',
    dateCreation: '2026-05-02',
    dateMiseAJour: '2026-05-04',
    site: 'Centre de réparation Aubagne',
    stock: 'Box de Jérome',
    superviseur: 'Thomas Leroy',
    inventoriste: 'Julie Martin',
    ouvertPar: 'Thomas Leroy',
    emplacement: 'B4-C1-09',
    nombreArticles: 24,
    quantiteTheorique: 80,
    quantiteComptee: 80,
    sommePaHt: 2140.0,
    sommePvTtc: 3769.2,
    ecart: 0,
    statut: 'Clôturé',
    dateComptage: '2026-05-02',
  },
  {
    reference: 'INV-2026-003',
    nom: 'Inventaire camion Jamel',
    dateCreation: '2026-05-03',
    dateMiseAJour: '2026-05-06',
    site: 'Camion Jamel',
    stock: 'Camion Jamel',
    superviseur: 'Sophie Bernard',
    inventoriste: 'Jamel Messaoudi',
    ouvertPar: 'Sophie Bernard',
    emplacement: 'C2-A7-02',
    nombreArticles: 52,
    quantiteTheorique: 200,
    quantiteComptee: 197,
    sommePaHt: 4125.75,
    sommePvTtc: 7384.6,
    ecart: -3,
    statut: 'En cours',
    dateComptage: '',
  },
  {
    reference: 'INV-2026-004',
    nom: 'Inventaire camion José',
    dateCreation: '2026-04-25',
    dateMiseAJour: '2026-04-29',
    site: 'Camion José',
    stock: 'Camion José',
    superviseur: 'Marc Fontaine',
    inventoriste: 'José Alvarez',
    ouvertPar: 'Marc Fontaine',
    emplacement: 'D3-D4-11',
    nombreArticles: 15,
    quantiteTheorique: 45,
    quantiteComptee: 46,
    sommePaHt: 980.4,
    sommePvTtc: 1695.7,
    ecart: 1,
    statut: 'Clôturé',
    dateComptage: '2026-04-29',
  },
  {
    reference: 'INV-2026-005',
    nom: 'Inventaire Box Lucie — Argenteuil',
    dateCreation: '2026-05-19',
    dateMiseAJour: '2026-05-19',
    site: 'Auchan Argenteuil',
    stock: 'Box de Lucie',
    superviseur: 'Élise Garnier',
    inventoriste: 'Camille Robert',
    ouvertPar: 'Élise Garnier',
    emplacement: '',
    nombreArticles: 31,
    quantiteTheorique: 95,
    quantiteComptee: 0,
    sommePaHt: 2750.0,
    sommePvTtc: 4920.0,
    ecart: -95,
    statut: 'Nouveau',
    dateComptage: '',
  },
  getDemoInventoryListRow(),
];

const formatDate = (dateStr: string): string => {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr });
  } catch {
    return dateStr;
  }
};

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);

const getOptionValue = (option: unknown): string => {
  if (!option || typeof option !== 'object' || !('value' in option)) {
    return '';
  }

  const value = (option as { value?: unknown }).value;

  return typeof value === 'string' ? value : '';
};

const getSelectedOption = (
  options: FilterOption[],
  value: string,
): FilterOption | undefined => options.find((option) => option.value === value);

const FiltersContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const $FiltersToolbar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 1rem 0;
  align-items: center;
  flex-wrap: wrap;
`;

/** Défilement horizontal lorsque le tableau est plus large que l’écran (tableau responsive). */
const $InventoryTableScrollArea = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
`;

const FilterButtonStyled = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  padding: 7px 8px;
  background: ${({ theme }) => theme.systemColors?.defaultBackground || '#fff'};
  border: 1px solid ${({ $isActive }) => ($isActive ? '#a2abe9' : '#ccd2f9')};
  border-radius: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors?.mainTextColor || '#333'};
  cursor: pointer;
  height: 32px;
`;

const FilterNumber = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 4px 1px;
  margin-left: 8px;
  background: #5369eb;
  border-radius: 4px;
  font-size: 10px;
  color: #fff;
`;

const FilterIconWrapper = styled.div`
  margin-right: 0.3rem;
  display: flex;
  align-items: center;
`;

const FilterChevronWrapper = styled.div<{ $isMenuOpen?: boolean }>`
  display: flex;
  margin-left: 0.25rem;
  transition: transform 150ms cubic-bezier(0.47, 1.64, 0.41, 0.8);
  transform: rotate(${({ $isMenuOpen }) => ($isMenuOpen ? '180deg' : '0deg')});
`;

const FilterMenuDropdown = styled.div<{ $isOpen: boolean; $top: number; $left: number }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  background: white;
  border: 1px solid #ccd2f9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 220px;
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
`;

const FilterMenuItem = styled.label`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.newUI?.variationColors?.light5 || '#f5f5f5'};
  }

  input[type='checkbox'] {
    margin-right: 0.5rem;
  }
`;

const RowActionContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const RowActionButton = styled.button`
  border: 1px solid #ccd2f9;
  background: #fff;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  color: #5c616b;
  font-size: 16px;
  line-height: 1;

  &:hover {
    border-color: #5a72ff;
    color: #5a72ff;
  }
`;

const RowActionMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  display: flex;
  flex-direction: column;
  min-width: 150px;
  background: #fff;
  border: 1px solid #ccd2f9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 20;
  padding: 0.25rem;
`;

const RowActionMenuItem = styled.button`
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.5rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #343434;

  &:hover {
    background: #f5f7ff;
  }
`;

const RowActionMenuItemDisabled = styled(RowActionMenuItem)`
  color: #9ca3af;
  cursor: not-allowed;

  &:hover {
    background: transparent;
  }
`;

const RowActionMenuItemDanger = styled(RowActionMenuItem)`
  color: #b91c1c;

  &:hover {
    background: #fef2f2;
    color: #991b1b;
  }
`;

const $EcartValue = styled.span<{ $variant: 'zero' | 'negative' | 'positive' }>`
  font-weight: 600;
  color: ${({ $variant }) => {
    if ($variant === 'zero') {
      return '#15803d';
    }
    if ($variant === 'negative') {
      return '#b91c1c';
    }

    return '#1d4ed8';
  }};
`;

const $StockSiteCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  line-height: 1.35;
`;

const $StockSiteSub = styled.span`
  font-size: 0.75rem;
  color: #64748b;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const $AmountEcartSub = styled($EcartValue)`
  font-size: 0.75rem;
  font-weight: 500;
`;

const normalizeText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const hasInventorySite = (site: unknown): boolean => normalizeText(site) !== '';

const toFormState = (inventory?: InventoryRow): InventoryFormState => ({
  nom: inventory?.nom ?? '',
  site: inventory?.site ?? '',
  stock: inventory?.stock ?? '',
  superviseur: inventory?.superviseur ?? '',
  inventoriste: inventory?.inventoriste ?? '',
});

const mergeMockAndStockLaunchedInventories = (): InventoryRow[] => [
  ...loadStockLaunchedInventories(),
  ...MOCK_INVENTORIES,
];

const enrichInventoryRow = (row: InventoryRow): InventoryRow =>
  applyClosureToInventoryRow(applyManualLinesToInventoryRow(applyInventoryNomToRow(row)));

const withManualPieceAdjustments = (rows: InventoryRow[]): InventoryRow[] =>
  rows.map(enrichInventoryRow);

/** Colonnes masquées en vue inventoriste sur le tableau des inventaires. */
const INVENTORISTE_HIDDEN_LIST_COLUMN_ACCESSORS = new Set([
  'quantiteTheorique',
  'quantiteComptee',
  'ecart',
]);

const INVENTORISTE_HIDDEN_LIST_COLUMN_IDS = new Set(['paPvTotals']);

const InventairePage = () => {
  const navigate = useNavigate();
  const currentUserDisplayName = useCurrentUserDisplayName();
  const { viewMode } = useInventoryViewMode();
  const [inventories, setInventories] = useState<InventoryRow[]>(() =>
    withManualPieceAdjustments(mergeMockAndStockLaunchedInventories()),
  );
  const [openFilterMenu, setOpenFilterMenu] = useState<InventoryFilterKey | null>(null);
  const [filterMenuPosition, setFilterMenuPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const filterMenuRef = useRef<HTMLDivElement | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<Record<InventoryFilterKey, string[]>>({
    nom: [],
    site: [],
    stock: [],
    statut: [],
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingReference, setEditingReference] = useState<string | null>(null);
  const [formState, setFormState] = useState<InventoryFormState>(toFormState());
  const [openRowActionId, setOpenRowActionId] = useState<string | null>(null);
  const [rowActionMenuPosition, setRowActionMenuPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const rowActionMenuRef = useRef<HTMLDivElement | null>(null);
  const deletedInventoryReferencesRef = useRef<Set<string>>(new Set());

  const centeredCellProps = useMemo(
    () => ({
      style: { verticalAlign: 'middle' as const },
    }),
    [],
  );

  const siteOptions = useMemo<FilterOption[]>(
    () =>
      Array.from(new Set(inventories.map((item) => item.site))).map((site) => ({
        value: site,
        label: site,
      })),
    [inventories],
  );

  const stockOptions = useMemo<FilterOption[]>(
    () =>
      Array.from(new Set(inventories.map((item) => item.stock))).map((stock) => ({
        value: stock,
        label: stock,
      })),
    [inventories],
  );

  const filterOptions = useMemo<Record<InventoryFilterKey, FilterOption[]>>(() => {
    const buildOptions = (
      key: InventoryFilterKey,
      formatter?: (value: string) => string,
    ): FilterOption[] => {
      const values = inventories.map((item) => item[key]);

      return Array.from(new Set(values)).map((value) => ({
        value,
        label: formatter ? formatter(value) : value,
        count: inventories.filter((item) => item[key] === value).length,
      }));
    };

    return {
      nom: buildOptions('nom').sort((a, b) => a.label.localeCompare(b.label, 'fr')),
      site: buildOptions('site'),
      stock: buildOptions('stock'),
      statut: buildOptions('statut'),
    };
  }, [inventories]);

  useEffect(() => {
    const reloadInventories = () => {
      const hidden = deletedInventoryReferencesRef.current;

      setInventories(
        withManualPieceAdjustments(mergeMockAndStockLaunchedInventories()).filter(
          (row) => !hidden.has(row.reference),
        ),
      );
    };

    window.addEventListener(POC_INVENTORIES_FROM_STOCK_EVENT, reloadInventories);
    window.addEventListener(POC_INVENTORY_MANUAL_PIECE_EVENT, reloadInventories);
    window.addEventListener(POC_INVENTORY_CLOSURE_EVENT, reloadInventories);
    window.addEventListener(POC_INVENTORY_NOM_EVENT, reloadInventories);

    return () => {
      window.removeEventListener(POC_INVENTORIES_FROM_STOCK_EVENT, reloadInventories);
      window.removeEventListener(POC_INVENTORY_MANUAL_PIECE_EVENT, reloadInventories);
      window.removeEventListener(POC_INVENTORY_CLOSURE_EVENT, reloadInventories);
      window.removeEventListener(POC_INVENTORY_NOM_EVENT, reloadInventories);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setOpenFilterMenu(null);
      }
    };

    if (openFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rowActionMenuRef.current && !rowActionMenuRef.current.contains(event.target as Node)) {
        setOpenRowActionId(null);
      }
    };

    if (openRowActionId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openRowActionId]);

  const handleFilterToggle = (filterType: InventoryFilterKey, value: string) => {
    setSelectedFilters((prev) => {
      const current = prev[filterType];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];

      return { ...prev, [filterType]: next };
    });
  };

  const handleFilterButtonClick = (
    filterType: InventoryFilterKey,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    const isOpen = openFilterMenu === filterType;

    if (isOpen) {
      setOpenFilterMenu(null);

      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();

    setFilterMenuPosition({ top: rect.bottom + 4, left: rect.left });
    setOpenFilterMenu(filterType);
  };

  const getFilterIcon = (filterType: InventoryFilterKey) => {
    const isActive = selectedFilters[filterType].length > 0;
    const color = isActive || openFilterMenu === filterType ? '#5a72ff' : '#5c616b';

    switch (filterType) {
      case 'nom':
        return <Search width="18px" height="18px" color={color} />;
      case 'site':
        return <Pin width="18px" height="18px" color={color} />;
      case 'stock':
        return <Bookmark width="18px" height="18px" color={color} />;
      case 'statut':
        return <Pin width="18px" height="18px" color={color} />;
      default:
        return null;
    }
  };

  const filteredInventories = useMemo(
    () =>
      inventories.filter((item) => {
        const matchesNom =
          selectedFilters.nom.length === 0 || selectedFilters.nom.includes(item.nom);
        const matchesSite =
          selectedFilters.site.length === 0 || selectedFilters.site.includes(item.site);
        const matchesStock =
          selectedFilters.stock.length === 0 || selectedFilters.stock.includes(item.stock);
        const matchesStatut =
          selectedFilters.statut.length === 0 || selectedFilters.statut.includes(item.statut);

        return matchesNom && matchesSite && matchesStock && matchesStatut;
      }),
    [inventories, selectedFilters],
  );

  const isFormValid =
    Boolean(formState.nom.trim()) &&
    Boolean(formState.superviseur.trim()) &&
    Boolean(formState.inventoriste.trim()) &&
    (Boolean(editingReference) || Boolean(formState.stock.trim()));

  const handleOpenCreate = () => {
    setEditingReference(null);
    setFormState(toFormState());
    setIsDrawerOpen(true);
  };

  const handleOpenEdit = (inventory: InventoryRow) => {
    if (inventory.statut !== 'Nouveau') {
      return;
    }

    setEditingReference(inventory.reference);
    setFormState(toFormState(inventory));
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingReference(null);
    setFormState(toFormState());
  };

  const handleDeleteInventory = (inventory: InventoryRow) => {
    if (
      !window.confirm(
        `Annuler l'inventaire « ${inventory.nom} » ? ` +
          'Les inventaires créés depuis le stock seront retirés du stockage local.',
      )
    ) {
      return;
    }

    deleteStockLaunchedInventoryByReference(inventory.reference);
    deletedInventoryReferencesRef.current.add(inventory.reference);
    setInventories((prev) => prev.filter((row) => row.reference !== inventory.reference));
    setOpenRowActionId(null);

    if (editingReference === inventory.reference) {
      handleCloseDrawer();
    }
  };

  const handleSaveInventory = () => {
    if (!isFormValid) {
      return;
    }
    const currentItem = editingReference
      ? inventories.find((item) => item.reference === editingReference)
      : undefined;
    const nextReference = editingReference ?? `INV-2026-${String(inventories.length + 1).padStart(3, '0')}`;
    const nextNom = formState.nom.trim();

    const nextItem: InventoryRow = {
      reference: nextReference,
      nom: nextNom,
      dateCreation: currentItem?.dateCreation ?? format(new Date(), 'yyyy-MM-dd'),
      dateMiseAJour: format(new Date(), 'yyyy-MM-dd'),
      site: editingReference ? (currentItem?.site ?? '') : formState.site.trim(),
      stock: editingReference ? (currentItem?.stock ?? '') : formState.stock.trim(),
      superviseur: formState.superviseur,
      inventoriste: formState.inventoriste,
      ouvertPar: currentItem?.ouvertPar ?? currentUserDisplayName,
      emplacement: currentItem?.emplacement ?? '',
      nombreArticles: currentItem?.nombreArticles ?? 0,
      quantiteTheorique: currentItem?.quantiteTheorique ?? 0,
      quantiteComptee: currentItem?.quantiteComptee ?? 0,
      sommePaHt: currentItem?.sommePaHt ?? 0,
      sommePvTtc: currentItem?.sommePvTtc ?? 0,
      ecart: currentItem?.ecart ?? 0,
      statut: currentItem?.statut ?? 'En cours',
      dateComptage: currentItem?.statut === 'Clôturé' ? currentItem.dateComptage : '',
    };

    persistInventoryNom(nextReference, nextNom);

    setInventories((prev) => {
      if (!editingReference) {
        return [...prev, nextItem];
      }

      return prev.map((item) => (item.reference === editingReference ? nextItem : item));
    });

    handleCloseDrawer();
  };

  const columns = useMemo<Column<InventoryRow>[]>(() => {
    const allColumns: Column<InventoryRow>[] = [
      {
        Header: 'Nom',
        accessor: 'nom',
        Cell: ({ row }: { row: { original: InventoryRow } }) => {
          const { nom, dateCreation } = row.original;

          return (
            <$StockSiteCell>
              <span>{nom}</span>
              {dateCreation ? (
                <$StockSiteSub>{`Créé le ${formatDate(dateCreation)}`}</$StockSiteSub>
              ) : null}
            </$StockSiteCell>
          );
        },
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Date de mise à jour',
        accessor: 'dateMiseAJour',
        Cell: ({ value }: { value: string }) => (value ? formatDate(value) : ''),
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Stock',
        id: 'stockSite',
        accessor: 'stock',
        Cell: ({ row }: { row: { original: InventoryRow } }) => {
          const { stock, site } = row.original;

          return (
            <$StockSiteCell>
              <span>{stock}</span>
              {hasInventorySite(site) ? <$StockSiteSub>{normalizeText(site)}</$StockSiteSub> : null}
            </$StockSiteCell>
          );
        },
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Ouvert par',
        accessor: 'ouvertPar',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Superviseur',
        accessor: 'superviseur',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Inventoriste',
        accessor: 'inventoriste',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Nb article',
        accessor: 'nombreArticles',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Qté théorique',
        accessor: 'quantiteTheorique',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Qté comptée',
        accessor: 'quantiteComptee',
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Écart',
        accessor: 'ecart',
        Cell: ({ row, value }: { row: { original: InventoryRow }; value: number }) => {
          if (row.original.statut === 'En cours' || row.original.statut === 'Nouveau') {
            return '';
          }

          const variant =
            value === 0 ? 'zero' : value < 0 ? 'negative' : 'positive';

          return <$EcartValue $variant={variant}>{value}</$EcartValue>;
        },
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Écart PA HT / PV TTC',
        id: 'paPvTotals',
        accessor: (row) => row.sommePaHt,
        Cell: ({ row }: { row: { original: InventoryRow } }) => {
          const amountEcarts = resolveInventoryAmountEcarts(row.original);

          if (!amountEcarts) {
            return '';
          }

          const paVariant = getAmountEcartVariant(amountEcarts.ecartPaHt);
          const pvVariant = getAmountEcartVariant(amountEcarts.ecartPvTtc);

          return (
            <$StockSiteCell>
              <$EcartValue $variant={paVariant}>
                PA HT {formatSignedAmount(amountEcarts.ecartPaHt, formatAmount)}
              </$EcartValue>
              <$AmountEcartSub $variant={pvVariant}>
                PV TTC {formatSignedAmount(amountEcarts.ecartPvTtc, formatAmount)}
              </$AmountEcartSub>
            </$StockSiteCell>
          );
        },
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Statut',
        accessor: 'statut',
        Cell: ({ value }: { value: InventoryRow['statut'] }) => (
          <Tag
            alert={value === 'En cours'}
            success={value === 'Clôturé'}
            primary={value === 'Nouveau'}
            withoutMargin
            isSmall
          >
            {value}
          </Tag>
        ),
        getProps: () => centeredCellProps,
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }: { row: { original: InventoryRow } }) => {
          const inventory = row.original;

          return (
            <RowActionContainer>
              <RowActionButton
                type="button"
                aria-label="Actions inventaire"
                onClick={(event) => {
                  event.stopPropagation();
                  const rect = event.currentTarget.getBoundingClientRect();

                  setRowActionMenuPosition({
                    top: rect.bottom + 4,
                    left: Math.max(8, rect.right - 150),
                  });
                  setOpenRowActionId((prev) => (prev === inventory.reference ? null : inventory.reference));
                }}
              >
                ...
              </RowActionButton>
            </RowActionContainer>
          );
        },
        getProps: () => centeredCellProps,
      },
    ];

    if (viewMode === 'inventoriste') {
      return allColumns.filter((column) => {
        if (column.id && INVENTORISTE_HIDDEN_LIST_COLUMN_IDS.has(column.id)) {
          return false;
        }

        const accessor = column.accessor;

        return typeof accessor !== 'string' || !INVENTORISTE_HIDDEN_LIST_COLUMN_ACCESSORS.has(accessor);
      });
    }

    return allColumns;
  }, [centeredCellProps, viewMode]);

  const selectedInventoryForAction = openRowActionId
    ? inventories.find((item) => item.reference === openRowActionId) ?? null
    : null;

  return (
    <div style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      <div style={{ marginTop: '2rem' }}>
        <Panel>
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <Heading level={2}>Tableau d&apos;inventaires</Heading>
            <Button type={BUTTON_TYPES.BUTTON} primary onClick={handleOpenCreate}>
              Nouveau
            </Button>
          </div>
          <$FiltersToolbar>
            <FiltersContainer>
              {(['nom', 'site', 'stock', 'statut'] as const).map((filterType) => {
                const isMenuOpen = openFilterMenu === filterType;
                const selectedCount = selectedFilters[filterType].length;
                const labels: Record<InventoryFilterKey, string> = {
                  nom: 'Nom',
                  site: 'Site',
                  stock: 'Stock',
                  statut: 'Statut',
                };

                return (
                  <div
                    key={filterType}
                    ref={filterType === openFilterMenu ? filterMenuRef : null}
                    style={{ position: 'relative' }}
                  >
                    <FilterButtonStyled
                      $isActive={selectedCount > 0 || isMenuOpen}
                      onClick={(event) => handleFilterButtonClick(filterType, event)}
                    >
                      <FilterIconWrapper>{getFilterIcon(filterType)}</FilterIconWrapper>
                      <span>{labels[filterType]}</span>
                      {selectedCount > 0 && <FilterNumber>{selectedCount}</FilterNumber>}
                      <FilterChevronWrapper $isMenuOpen={isMenuOpen}>
                        <ChevronDownIcon size="16px" />
                      </FilterChevronWrapper>
                    </FilterButtonStyled>

                    {isMenuOpen && (
                      <FilterMenuDropdown
                        $isOpen={isMenuOpen}
                        $top={filterMenuPosition.top}
                        $left={filterMenuPosition.left}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {filterOptions[filterType].map((option) => {
                          const isSelected = selectedFilters[filterType].includes(option.value);

                          return (
                            <FilterMenuItem key={`${filterType}-${option.value}`}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleFilterToggle(filterType, option.value)}
                              />
                              <span>
                                {option.label} ({option.count ?? 0})
                              </span>
                            </FilterMenuItem>
                          );
                        })}
                      </FilterMenuDropdown>
                    )}
                  </div>
                );
              })}
            </FiltersContainer>
          </$FiltersToolbar>
          <$InventoryTableScrollArea>
            <Table<InventoryRow>
              key={viewMode}
              columns={columns}
              data={filteredInventories}
              dataTestId="inventory-table"
            />
          </$InventoryTableScrollArea>
          {selectedInventoryForAction && (
            <RowActionMenu
              ref={rowActionMenuRef}
              $top={rowActionMenuPosition.top}
              $left={rowActionMenuPosition.left}
            >
              <RowActionMenuItem
                type="button"
                onClick={() => {
                  setOpenRowActionId(null);
                  navigate(`/inventaire/${selectedInventoryForAction.reference}`);
                }}
              >
                Ouvrir
              </RowActionMenuItem>
              {selectedInventoryForAction.statut === 'Clôturé' ? (
                <>
                  <RowActionMenuItemDisabled type="button">
                    Modifier (clôturé)
                  </RowActionMenuItemDisabled>
                  <RowActionMenuItem
                    type="button"
                    onClick={() => {
                      setOpenRowActionId(null);
                      alert(
                        `Téléchargement du reçu fiscal pour ${selectedInventoryForAction.reference} (mock).`,
                      );
                    }}
                  >
                    Télécharger le reçu fiscal
                  </RowActionMenuItem>
                </>
              ) : selectedInventoryForAction.statut === 'En cours' ? (
                <RowActionMenuItemDisabled type="button">
                  Modifier (en cours)
                </RowActionMenuItemDisabled>
              ) : (
                <RowActionMenuItem
                  type="button"
                  onClick={() => {
                    setOpenRowActionId(null);
                    handleOpenEdit(selectedInventoryForAction);
                  }}
                >
                  Modifier
                </RowActionMenuItem>
              )}
              <RowActionMenuItemDanger
                type="button"
                data-testid="inventory-row-delete"
                onClick={() => {
                  handleDeleteInventory(selectedInventoryForAction);
                }}
              >
                Annuler
              </RowActionMenuItemDanger>
            </RowActionMenu>
          )}
        </Panel>
      </div>
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        position={DrawerPosition.Right}
        width={520}
      >
        <Heading level={2}>
          {editingReference ? 'Modifier un inventaire' : 'Créer un inventaire'}
        </Heading>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
          <div>
            <div style={{ marginBottom: '0.4rem', fontWeight: 500 }}>Nom de l&apos;inventaire</div>
            <Input
              name="inventory-upsert-nom"
              value={formState.nom}
              placeholder="Ex. Inventaire Box Martin — mai 2026"
              onChange={(event) => {
                setFormState((prev) => ({ ...prev, nom: event.target.value }));
              }}
            />
          </div>
          {!editingReference ? (
            <>
              <div>
                <div style={{ marginBottom: '0.4rem', fontWeight: 500 }}>Site</div>
                <Autocomplete
                  name="inventory-upsert-site"
                  options={siteOptions}
                  value={getSelectedOption(siteOptions, formState.site)}
                  placeholder="Sélectionner un site (optionnel)"
                  onChange={(option) => {
                    setFormState((prev) => ({ ...prev, site: getOptionValue(option) }));
                  }}
                />
              </div>
              <div>
                <div style={{ marginBottom: '0.4rem', fontWeight: 500 }}>Stock</div>
                <Autocomplete
                  name="inventory-upsert-stock"
                  options={stockOptions}
                  value={getSelectedOption(stockOptions, formState.stock)}
                  placeholder="Sélectionner un stock"
                  onChange={(option) => {
                    setFormState((prev) => ({ ...prev, stock: getOptionValue(option) }));
                  }}
                />
              </div>
            </>
          ) : null}
          <div>
            <div style={{ marginBottom: '0.4rem', fontWeight: 500 }}>Superviseur</div>
            <Autocomplete
              name="inventory-upsert-superviseur"
              options={SUPERVISEUR_OPTIONS}
              value={getSelectedOption(SUPERVISEUR_OPTIONS, formState.superviseur)}
              placeholder="Sélectionner un superviseur"
              onChange={(option) => {
                setFormState((prev) => ({ ...prev, superviseur: getOptionValue(option) }));
              }}
            />
          </div>
          <div>
            <div style={{ marginBottom: '0.4rem', fontWeight: 500 }}>Inventoriste</div>
            <Autocomplete
              name="inventory-upsert-inventoriste"
              options={INVENTORISTE_OPTIONS}
              value={getSelectedOption(INVENTORISTE_OPTIONS, formState.inventoriste)}
              placeholder="Sélectionner un inventoriste"
              onChange={(option) => {
                setFormState((prev) => ({ ...prev, inventoriste: getOptionValue(option) }));
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
            <Button type={BUTTON_TYPES.BUTTON} tertiary onClick={handleCloseDrawer}>
              Annuler
            </Button>
            <Button
              type={BUTTON_TYPES.BUTTON}
              primary
              onClick={handleSaveInventory}
              disabled={!isFormValid}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default InventairePage;
